// src/features/tidsregistering/components/day-overview/DayOverviewApprovalDialog.jsx
import React, { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Divider, Chip, Table,
  TableBody, TableCell, TableHead, TableRow, Alert,
  Select, MenuItem, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Edit as EditIcon,
  Check as ConfirmIcon,
  Close as CancelIcon,
  Refresh as RecalcIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import StaffMember from '../../../../components/common/StaffMember';
import { DEVIATION_LABELS, SEVERITY_ALERT } from '../../constants/severity';
import DayTimeline, { resolveColor } from './DayTimeline';
import { SHIFT_COLOR } from '../../constants/colors';

const hhmm = (t) => String(t).slice(0, 5);

function plannedHours(shift) {
  const [sh, sm] = hhmm(shift.start_time).split(':').map(Number);
  const [eh, em] = hhmm(shift.end_time).split(':').map(Number);
  return ((eh * 60 + em) - (sh * 60 + sm) - (shift.break_minutes ?? 0)) / 60;
}

function CodeChip({ code }) {
  if (!code) return <Typography variant="body2">—</Typography>;
  const color = resolveColor(code);
  return (
    <Chip
      size="small"
      label={code.code ? `${code.code} ${code.title}` : code.title}
      sx={{ bgcolor: color, color: 'common.white', fontWeight: 600 }}
    />
  );
}

function LegendItem({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

function ApprovalRow({ label, approved, changedAt }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {approved
        ? <CheckCircleIcon fontSize="small" color="success" />
        : <UncheckedIcon fontSize="small" color="disabled" />}
      <Typography variant="body2">
        {label}
        {approved && changedAt && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {format(new Date(changedAt), 'dd/MM/yyyy HH:mm')}
          </Typography>
        )}
      </Typography>
    </Box>
  );
}

function TimeDetailRow({ td, registrationCodes, onReclassify, reclassifyLoading }) {
  const [editing, setEditing] = useState(false);
  const [selectedCode, setSelectedCode] = useState(td.registration_code_id);

  const handleConfirm = useCallback(() => {
    if (selectedCode !== td.registration_code_id) {
      onReclassify(td.time_detail_id, selectedCode);
    }
    setEditing(false);
  }, [selectedCode, td, onReclassify]);

  const handleCancel = () => {
    setSelectedCode(td.registration_code_id);
    setEditing(false);
  };

  // Only show codes that are time-detail eligible
  const eligibleCodes = registrationCodes.filter((c) => c.is_time_detail_code !== false);

  const currentCode = registrationCodes.find((c) => c.registration_code_id === td.registration_code_id);

  return (
    <TableRow>
      <TableCell>{String(td.start_time).slice(0, 5)}</TableCell>
      <TableCell>{String(td.end_time).slice(0, 5)}</TableCell>
      <TableCell align="right">{Number(td.hours_sum).toFixed(2)}t</TableCell>
      <TableCell>
        {editing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Select
              size="small"
              value={selectedCode}
              onChange={(e) => setSelectedCode(e.target.value)}
              sx={{ minWidth: 180, fontSize: 12 }}
            >
              {eligibleCodes.map((c) => (
                <MenuItem key={c.registration_code_id} value={c.registration_code_id} sx={{ fontSize: 12 }}>
                  {c.title}
                </MenuItem>
              ))}
            </Select>
            <IconButton size="small" color="success" onClick={handleConfirm} disabled={reclassifyLoading}>
              {reclassifyLoading ? <CircularProgress size={14} /> : <ConfirmIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={handleCancel}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CodeChip code={currentCode} />
            <Tooltip title="Omklassificer">
              <IconButton size="small" onClick={() => setEditing(true)}>
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function DayOverviewApprovalDialog({
  open,
  overview,
  staffMember,
  registrationCodes = [],
  plannedShift = null,
  onClose,
  onApprove,
  onReclassify,
  onRecalculate,
  approveLoading,
  reclassifyLoading,
  recalcLoading,
}) {
  if (!overview) return null;

  const diff = Number(overview.hours_diff_from_scheduled);
  const diffLabel = diff === 0 ? '–' : `${diff > 0 ? '+' : ''}${diff.toFixed(2)}t`;

  const latestDetails = (overview.time_details ?? []).filter((td) => td.is_latest_version !== false);
  const codeMap = new Map(registrationCodes.map((c) => [c.registration_code_id, c]));
  const usedCodes = [...new Set(latestDetails.map((td) => td.registration_code_id))]
    .map((id) => codeMap.get(id))
    .filter(Boolean);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">Dag-oversigt</Typography>
            <Typography variant="body2" color="text.secondary">
              {format(new Date(overview.date), 'EEEE d. MMMM yyyy')}
            </Typography>
          </Box>
          {staffMember && (
            <StaffMember staffMember={staffMember} avatarSize={32} gap={1} clickable={false} />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Summary */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Planlagt</Typography>
            {plannedShift ? (
              <>
                <Typography variant="h6">
                  {hhmm(plannedShift.start_time)}–{hhmm(plannedShift.end_time)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {plannedHours(plannedShift).toFixed(2)}t
                  {plannedShift.break_minutes ? `, ${plannedShift.break_minutes} min pause` : ''}
                </Typography>
              </>
            ) : (
              <Typography variant="h6" color="text.disabled">Ingen vagt</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Timer arbejdet</Typography>
            <Typography variant="h6">{Number(overview.hours_sum).toFixed(2)}t</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Afvigelse fra plan</Typography>
            <Typography variant="h6" color={diff > 0 ? 'warning.main' : diff < 0 ? 'error.main' : 'text.primary'}>
              {diffLabel}
            </Typography>
          </Box>
          {overview.first_registration && (
            <Box>
              <Typography variant="caption" color="text.secondary">Check ind</Typography>
              <Typography variant="h6">{String(overview.first_registration).slice(0, 5)}</Typography>
            </Box>
          )}
          {overview.last_registration && (
            <Box>
              <Typography variant="caption" color="text.secondary">Check ud</Typography>
              <Typography variant="h6">{String(overview.last_registration).slice(0, 5)}</Typography>
            </Box>
          )}
        </Box>

        {/* Day timeline */}
        <Box sx={{ mb: 2 }}>
          <DayTimeline
            timeDetails={overview.time_details ?? []}
            registrationCodes={registrationCodes}
            plannedShifts={plannedShift ? [plannedShift] : []}
            height={24}
            showAxis
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            {plannedShift && <LegendItem color={SHIFT_COLOR} label="Planlagt vagt" />}
            {usedCodes.map((code) => (
              <LegendItem key={code.registration_code_id} color={resolveColor(code)} label={code.title} />
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Time details with reclassification */}
        <Typography variant="subtitle2" gutterBottom>Tidsdetaljer</Typography>
        {overview.time_details?.length > 0 ? (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Start</TableCell>
                <TableCell>Slut</TableCell>
                <TableCell align="right">Timer</TableCell>
                <TableCell>Kode</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overview.time_details
                .filter((td) => td.is_latest_version)
                .sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)))
                .map((td) => (
                  <TimeDetailRow
                    key={td.time_detail_id}
                    td={td}
                    registrationCodes={registrationCodes}
                    onReclassify={onReclassify}
                    reclassifyLoading={reclassifyLoading}
                  />
                ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>Ingen tidsdetaljer</Typography>
        )}

        {/* Deviations */}
        {overview.deviations?.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Afvigelser</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {overview.deviations.map((dev) => (
                <Alert key={dev.deviation_id} severity={SEVERITY_ALERT[dev.severity_code] ?? 'info'} sx={{ py: 0 }}>
                  <Typography variant="body2">
                    <strong>{DEVIATION_LABELS[dev.type] ?? dev.type}:</strong> {dev.message}
                  </Typography>
                </Alert>
              ))}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Approval status */}
        <Typography variant="subtitle2" gutterBottom>Godkendelsesstatus</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <ApprovalRow label="Medarbejder godkendelse" approved={overview.approved_by_user} changedAt={overview.user_approval_changed} />
          <ApprovalRow label="Admin godkendelse" approved={overview.approved_by_admin} changedAt={overview.admin_approval_changed} />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Luk</Button>
        {overview.approved_by_admin && onRecalculate && (
          <Button
            variant="outlined"
            color="info"
            startIcon={recalcLoading ? <CircularProgress size={16} color="inherit" /> : <RecalcIcon />}
            disabled={recalcLoading}
            onClick={() => onRecalculate(overview.day_overview_id)}
          >
            Genberegn saldo
          </Button>
        )}
        {!overview.approved_by_admin && (
          <Button
            variant="contained"
            color="success"
            startIcon={approveLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            disabled={approveLoading}
            onClick={() => onApprove(overview.day_overview_id)}
          >
            Godkend som admin
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
